import React from 'react';
import classNames from 'classnames';
import ReactTextareaAutosize, { TextareaAutosizeProps } from 'react-textarea-autosize';
import { Control, useController, UseControllerProps } from 'react-hook-form';
import styles from './Textarea.module.scss';
import Icon from '../Icon/Icon';

export interface TextareaProps extends TextareaAutosizeProps {
  label?: string;
  className?: string;
  addPaddingForIconRight?: boolean;
  error?: boolean;
  errorMessage?: string;
  noMinHeight?: boolean;
  isInfoDisplay?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      className,
      addPaddingForIconRight,
      error = false,
      noMinHeight = false,
      isInfoDisplay = false,
      errorMessage,
      ...rest
    },
    ref,
  ) => {
    const [hasError, setHasError] = React.useState<boolean>(error);

    const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (hasError) setHasError(false);

      if (rest.onChange) rest.onChange(e);
    };

    return (
      <div className={classNames(styles.root, className, { [styles.infoDisplay]: isInfoDisplay })}>
        {label && <span className="typo-text-14--bold text-faded mb-12">{label}</span>}
        <ReactTextareaAutosize
          onChange={onChange}
          {...rest}
          ref={ref}
          className={classNames(
            styles.textarea,
            addPaddingForIconRight && styles.addPaddingForIconRight,
            !noMinHeight && styles.minHeight,
            {
              [styles.error]: hasError,
            },
          )}
          rows={4}
          maxRows={10}
        />
        {error && (
          <span className="typo-text-12--bold status-error-full mt-8 flex items-center gap-x-4">
            <Icon width={16} height={16} name="error" />
            {errorMessage}
          </span>
        )}
      </div>
    );
  },
);

export interface ControlledTextareaProps extends Omit<TextareaProps, 'defaultValue'>, UseControllerProps {
  name: string; // Ensures the controlled component has a name for react-hook-form
  control: Control<any>; // Ensures the controlled component has a control for react-hook-form
}

export const ControlledTextarea: React.FC<ControlledTextareaProps> = ({
  name,
  control,
  defaultValue = '',
  ...props
}) => {
  const {
    field: { ref, value, onChange, onBlur },
    fieldState: { error },
  } = useController({ name, control, defaultValue });

  return (
    <Textarea
      {...props}
      ref={ref}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      error={!!error}
      errorMessage={error?.message}
    />
  );
};

export default Textarea;
